import { type apiClient, query } from '@app/icare/client/api'
import { NotificationsDrawer } from '@app/icare/components/notification/NotificationsDrawer'
import TherapyStartBadge from '@app/icare/components/status/TherapyStartBadge'
import { formatPatientTitle } from '@app/icare/entities/patient/components/PatientTitle'
import { PatientStatusEnum } from '@app/icare/entities/patient/schema/Patient'
import type { PatientTherapyRelation } from '@app/icare/entities/patient/schema/PatientTherapy'
import { NotificationVoteType } from '@app/icare/entities/patientNotification/schema/PatientNotification'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { FilterButton } from '@pkg/ui/button'
import { SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Space, Table, Typography } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/notifications')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum(['patient.personal.lastName', 'patient.nextTherapyStart', 'patient.notifications.length'])
    .default('patient.personal.lastName')
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
      display: Q.eq(z.enum(['date', 'days'])).nullish(),
    })
    .optional(),
})

const useNotificationsQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: {
      status: F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
      display: F.eq('date'),
    },
  },
})

type GroupedNotificationsResponse = Awaited<ReturnType<typeof apiClient.patientNotifications.listGroupedByPatient>>
type NotificationPatientRow = GroupedNotificationsResponse['data'][number]
type NotificationSortBy = NonNullable<z.infer<typeof FilterSchema>['sortBy']>
type ApiNotificationSortBy = Exclude<NotificationSortBy, 'patient.nextTherapyStart' | 'patient.notifications.length'>

function getUrgencyScore(therapies: NotificationPatientRow['therapies']) {
  const today = dayjs().startOf('day')
  const scores = (therapies ?? []).map(({ activeFrom }) =>
    activeFrom ? dayjs(activeFrom).startOf('day').diff(today, 'day') : Infinity,
  )

  return Math.min(...scores)
}

function RouteComponent() {
  const queryStore = useNotificationsQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const queryInput = {
    searchValue: queryStore.searchValue,
    deleted: queryStore.deleted,
    filters: queryStore.filters,
    sortBy: (queryStore.sortBy === 'patient.nextTherapyStart' || queryStore.sortBy === 'patient.notifications.length'
      ? undefined
      : (queryStore.sortBy ?? undefined)) as ApiNotificationSortBy | undefined,
    sortDirection: queryStore.sortDirection ?? undefined,
    pageSize: queryStore.pageSize,
  }

  const { data, isFetching, refetch } = useQuery(
    query.patientNotifications.listGroupedByPatient.queryOptions({
      input: {
        query: queryInput,
      },
      select: result => ({
        ...result,
        data:
          queryStore.sortBy === 'patient.nextTherapyStart'
            ? [...result.data].sort((a, b) => getUrgencyScore(a.therapies) - getUrgencyScore(b.therapies))
            : queryStore.sortBy === 'patient.notifications.length'
              ? [...result.data].sort((a, b) => {
                  const compareResult = (a.notifications.length ?? 0) - (b.notifications.length ?? 0)
                  return queryStore.sortDirection === 'descend' ? -compareResult : compareResult
                })
              : result.data,
        total: result.data.length,
        page: queryStore.page,
        pageSize: queryStore.pageSize,
        pageSizeOptions: [10, 20, 50, 100],
      }),
    }),
  )

  const columns = useColumnDef<NotificationPatientRow>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) => <FormattedPatientNameAndAddress patient={record} />,
      },
      {
        key: 'patient.nextTherapyStart',
        title: <Trans>nächster Therapiestart</Trans>,
        sortDirections: ['ascend'],
        render: (_: unknown, record: NotificationPatientRow) =>
          (record.therapies || []).length === 0 ? (
            <Typography.Text type="secondary">-</Typography.Text>
          ) : (
            <Space size="small" wrap>
              {(record.therapies || []).map((therapy: PatientTherapyRelation) => (
                <TherapyStartBadge
                  key={therapy.id}
                  value={therapy.activeFrom}
                  relative={queryStore.filters.display.value === 'days'}
                />
              ))}
            </Space>
          ),
      },

      {
        key: 'patient.notifications.length',
        title: <Trans>Meldungen</Trans>,
        render: (_, record) => record.notifications.length,
      },
      {
        key: 'notifications',
        title: <Trans>Bearbeitende</Trans>,
        sorter: false,
        render: (_, record) => {
          const initialAgg: Record<(typeof NotificationVoteType)[keyof typeof NotificationVoteType], Set<string>> = {
            [NotificationVoteType.PE]: new Set<string>(),
            [NotificationVoteType.MED]: new Set<string>(),
          }

          const agg = (record.notifications ?? []).reduce((acc, v) => {
            if (typeof v.statusForMed.assignedTo === 'string') {
              acc[NotificationVoteType.MED].add(v.statusForMed.assignedTo)
            }
            if (typeof v.statusForPe.assignedTo === 'string') {
              acc[NotificationVoteType.PE].add(v.statusForPe.assignedTo)
            }
            return acc
          }, initialAgg)

          const peUsers = agg[NotificationVoteType.PE] ?? new Set<string>()
          const medUsers = agg[NotificationVoteType.MED] ?? new Set<string>()

          const aggEntries = [
            [NotificationVoteType.PE, peUsers],
            [NotificationVoteType.MED, medUsers],
          ] as const

          return (
            <>
              {aggEntries.map(([type, users]) => (
                <div key={type} className="flex gap-x-2">
                  <span>{type === NotificationVoteType.PE ? <Trans>PE</Trans> : <Trans>MED</Trans>}:</span>
                  <div className="flex gap-x-1">
                    <span className={twMerge(['font-bold', users.size === 0 ? 'text-error-500' : 'text-green-400'])}>
                      {users.size === 0 ? <Trans>ungeklärt</Trans> : Array.from(users).join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )
        },
      },
      {
        key: 'actions',
        sorter: false,
        title: <Trans>Aktionen</Trans>,
        fixed: 'end',
        render: (_, record) => (
          <Button
            size="small"
            onClick={() =>
              void NotificationsDrawer.show({
                patientId: record.id,
                patientName: formatPatientTitle(record),
                onClose: () => {
                  void refetch()
                },
              })
            }
          >
            <Trans>Meldungen anzeigen</Trans>
          </Button>
        ),
      },
    ],
    [queryStore.sortBy, queryStore.sortDirection, queryStore.filters.display],
    useNotificationsQueryStore,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space size="small">
            <SearchInput
              size="small"
              value={queryStore.meta.searchRawValue}
              onChange={e => queryStore.setSearch(e.target.value)}
            />
            <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
          </Space>
        ),
      },
    },
    [queryStore.meta.searchRawValue, queryStore.setSearch, queryStore.meta.activeFilterCount, setOpen, queryStore],
  )

  return (
    <QueryFilterWrapper
      queryStore={useNotificationsQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'status',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'display',
          label: <Trans>Darstellung nächster Therapiestart</Trans>,
          mode: 'single',
          options: [
            { label: <Trans>Datum</Trans>, value: 'date' },
            { label: <Trans>relativ</Trans>, value: 'days' },
          ],
        },
      ]}
    >
      <Table
        dataSource={data?.data ?? []}
        loading={isFetching}
        columns={columns}
        rowKey="id"
        size="small"
        onChange={queryStore.onChange}
        scroll={{ x: 'max-content' }}
      />
    </QueryFilterWrapper>
  )
}
